// HdButton — the canonical Helpdesk button.
//
// Translated from docs/design_handoff_helpdesk/prototype/primitives.jsx `Btn`.
// Every number below traces to that prototype; colours are read as CSS custom
// properties from the .helpdesk-shell block, never hard-coded.
//
// WHY THIS EXISTS: the first pass translated the five display atoms (StatusPill,
// PriorityMark, SlaFlag, SlaClock, ChannelTag) but not `Btn`, so 72 raw MUI
// <Button>s were hand-styled across 23 files — 33 "outlined", 24 "contained",
// 20 "text" with no rule mapping them to the design. Colour, height and label
// drifted page to page. Route every Helpdesk button through this component.
//
// TWO DELIBERATE DIVERGENCES from primitives.jsx, both documented rather than
// silent:
//
//  1. `size` defaults to 'sm', not 'md'. The prototype's Btn defaults to md, but
//     EVERY button in every screens-*.jsx passes size="sm" (xs is used only for
//     inline row actions like Edit / Remove / Profile). Defaulting to the value
//     the design actually renders keeps 72 call sites terse and removes the most
//     likely migration mistake. md/lg remain available.
//
//  2. Hover / focus-visible / active states are added. The prototype is a static
//     mock and defines none, but a real button needs them. They are derived from
//     the same tokens — no new colours are introduced.
//
// Wraps MUI Button rather than a bare <span> (which is what the prototype uses)
// so that onClick, disabled, type="submit", href, startIcon/endIcon, keyboard
// activation and focus management all keep working. MUI's own visual defaults
// (uppercase text, 64px min-width, elevation) are overridden.
import React from 'react';
import { Button, ButtonProps } from '@mui/material';

export type HdButtonVariant = 'primary' | 'secondary' | 'ghost' | 'accent' | 'danger';
export type HdButtonSize = 'xs' | 'sm' | 'md' | 'lg';

// primitives.jsx line 213 — padding / font-size / min-height per size.
const SIZES: Record<HdButtonSize, { padding: string; fontSize: number; minHeight: number }> = {
  xs: { padding: '3px 8px', fontSize: 12, minHeight: 24 },
  sm: { padding: '5px 10px', fontSize: 12.5, minHeight: 28 },
  md: { padding: '8px 14px', fontSize: 13, minHeight: 36 },
  lg: { padding: '10px 18px', fontSize: 14, minHeight: 42 }
};

// primitives.jsx lines 215-221. `hoverBackground` is the documented addition.
const VARIANTS: Record<
  HdButtonVariant,
  { background: string; color: string; border: string; hoverBackground: string }
> = {
  primary: {
    background: 'var(--ink)',
    color: 'var(--card)',
    border: '1px solid var(--ink)',
    hoverBackground: 'var(--ink-2)'
  },
  secondary: {
    background: 'var(--card)',
    color: 'var(--ink-2)',
    border: '1px solid var(--hairline-strong)',
    hoverBackground: 'var(--card-sub)'
  },
  ghost: {
    background: 'transparent',
    color: 'var(--ink-2)',
    border: '1px solid transparent',
    hoverBackground: 'var(--card-sub)'
  },
  accent: {
    background: 'var(--accent)',
    color: 'var(--card)',
    border: '1px solid var(--accent)',
    hoverBackground: 'var(--accent)'
  },
  danger: {
    background: 'transparent',
    color: 'var(--status-danger-fg)',
    border: '1px solid var(--status-danger-bg)',
    hoverBackground: 'var(--status-danger-bg)'
  }
};

export interface HdButtonProps extends Omit<ButtonProps, 'variant' | 'size' | 'color'> {
  /** Design variant. Exactly one `primary` per surface — the commit action
   *  (Send, Save rule, Approve, Resolve, New ticket). Everything else is
   *  `secondary` (Cancel, Clear filters, Back, Attach). `danger` is for
   *  destructive actions only (Reject, Remove). */
  hdVariant?: HdButtonVariant;
  /** Defaults to 'sm' — see the note at the top of this file. Use 'xs' only for
   *  inline row actions inside a table or list item. */
  hdSize?: HdButtonSize;
  /** Full-width (prototype's `full` prop). */
  full?: boolean;
}

export default function HdButton({
  hdVariant = 'secondary',
  hdSize = 'sm',
  full,
  sx,
  ...rest
}: HdButtonProps) {
  const s = SIZES[hdSize];
  const v = VARIANTS[hdVariant];

  return (
    <Button
      disableElevation
      disableRipple
      {...rest}
      sx={{
        // Layout — primitives.jsx lines 223-226.
        display: full ? 'flex' : 'inline-flex',
        width: full ? '100%' : 'auto',
        minWidth: 0, // MUI defaults to 64px, which stretches short labels like "Go".
        alignItems: 'center',
        justifyContent: 'center',
        gap: '6px',
        padding: s.padding,
        minHeight: s.minHeight,
        borderRadius: 'var(--r-sm)',
        fontSize: s.fontSize,
        fontWeight: 600,
        fontFamily: 'var(--font-sans)',
        lineHeight: 1.4,
        textTransform: 'none', // MUI uppercases by default; the design never does.
        whiteSpace: 'nowrap',
        boxShadow: 'none',

        // Variant
        backgroundColor: v.background,
        color: v.color,
        border: v.border,

        '&:hover': {
          backgroundColor: v.hoverBackground,
          borderColor: hdVariant === 'secondary' ? 'var(--muted-2)' : undefined,
          boxShadow: 'none'
        },
        '&:active': { transform: 'translateY(0.5px)' },
        '&.Mui-focusVisible': {
          outline: '2px solid var(--accent)',
          outlineOffset: '1px'
        },
        // primitives.jsx line 226 — disabled is opacity, not a colour swap, so a
        // disabled primary still reads as the primary action.
        '&.Mui-disabled': {
          opacity: 0.45,
          backgroundColor: v.background,
          color: v.color,
          border: v.border
        },

        // MUI puts 8px margins on start/end icons; the design uses a flat 6px gap.
        '& .MuiButton-startIcon, & .MuiButton-endIcon': { margin: 0 },
        '& .MuiButton-startIcon > *:nth-of-type(1), & .MuiButton-endIcon > *:nth-of-type(1)': {
          fontSize: s.fontSize + 2
        },

        ...sx
      }}
    />
  );
}
